/**
 * Bit-level writer/reader. (§2.8 — "Binary protocol only. No JSON on the wire,
 * ever. Use a bit-writer.")
 *
 * ── Why arithmetic instead of bitwise for the accumulator ──────────────────
 *
 * JS bitwise operators coerce to int32. The accumulator here legitimately holds
 * up to 39 bits (7 pending + a 32-bit write), so `<<` and `&` would silently
 * truncate it and corrupt every packet that straddles a byte boundary — a bug
 * that only shows up on some field alignments, which is the worst kind.
 *
 * So the accumulator uses `+`, `*`, `%` and Math.floor against a table of exact
 * powers of two. Every intermediate stays below 2^40, well inside the 2^53 range
 * where f64 integer arithmetic is exact, so the round-trip is bit-exact. The
 * bitwise ops that DO appear below are all provably confined to ≤32-bit values.
 *
 * ── Bit order ──────────────────────────────────────────────────────────────
 *
 * LSB-first: the first bit written lands in bit 0 of byte 0. This is why
 * peekPacketType() in protocol.ts can read the 4-bit message type straight out
 * of `bytes[0] & 0x0f` without spinning up a reader. Changing the bit order
 * breaks that (and every packet in flight) — it is part of the wire format.
 *
 * Zero allocation per packet: construct once, reset() per packet. (§15.7)
 */

/** Exact powers of two, 2^0 .. 2^32. Indexed only with provably in-range values. */
const TWO_POW: readonly number[] = (() => {
  const t: number[] = new Array<number>(33);
  for (let i = 0; i <= 32; i++) t[i] = 2 ** i;
  return t;
})();

/**
 * Default packet buffer size. Comfortably above the ~500-byte worst-case full
 * 22-player snapshot (see snapshot.bandwidth.test.ts) and below the ~1200-byte
 * MTU-safe payload of an unreliable WebRTC datachannel.
 */
export const DEFAULT_PACKET_CAPACITY = 2048;

/** Scratch for reinterpreting an f32 as a u32. Module scope: no per-call alloc. */
const scratchF32 = new Float32Array(1);
const scratchF32AsU32 = new Uint32Array(scratchF32.buffer);

// ---------------------------------------------------------------------------
// Writer
// ---------------------------------------------------------------------------

export class BitWriter {
  private readonly bytes: Uint8Array;
  private readonly capacity: number;
  /** Next byte index to emit into. */
  private bytePos = 0;
  /** Pending bits, LSB-aligned. Always < 2^accBits, and accBits < 8 at rest. */
  private acc = 0;
  private accBits = 0;

  /**
   * Set once the writer has run out of buffer. Every subsequent write is
   * dropped rather than wrapping or throwing: a caller that ignores this and
   * ships the packet gets a short packet the reader will reject, which is a far
   * better failure than silent corruption.
   */
  overflow = false;

  constructor(capacityBytes: number = DEFAULT_PACKET_CAPACITY) {
    this.bytes = new Uint8Array(capacityBytes);
    this.capacity = capacityBytes;
  }

  /** Rewind for the next packet. Does not clear the buffer — writes are assignments, not ORs. */
  reset(): void {
    this.bytePos = 0;
    this.acc = 0;
    this.accBits = 0;
    this.overflow = false;
  }

  /** Bits written so far, including the not-yet-emitted partial byte. */
  get bitLength(): number {
    return this.bytePos * 8 + this.accBits;
  }

  /** Bytes the packet will occupy once finished. */
  get byteLength(): number {
    return this.bytePos + (this.accBits > 0 ? 1 : 0);
  }

  get capacityBytes(): number {
    return this.capacity;
  }

  /**
   * Append the low `bits` bits of `value`.
   * @param bits 1..32. Values outside that range are a programmer error, not a
   *             runtime condition, so they throw rather than set `overflow`.
   */
  writeBits(value: number, bits: number): void {
    if (bits <= 0 || bits > 32) {
      throw new RangeError(`BitWriter.writeBits: bits must be 1..32, got ${bits}`);
    }
    if (this.overflow) return;

    // Mask to `bits`. `(1 << 32) - 1` is 0 in JS, so 32 is special-cased.
    const v = bits === 32 ? value >>> 0 : (value & ((1 << bits) - 1)) >>> 0;

    // accBits < 8 here (invariant), so TWO_POW index is 0..7 and acc stays < 2^40.
    this.acc += v * TWO_POW[this.accBits]!;
    this.accBits += bits;

    while (this.accBits >= 8) {
      if (this.bytePos >= this.capacity) {
        this.overflow = true;
        this.acc = 0;
        this.accBits = 0;
        return;
      }
      this.bytes[this.bytePos++] = this.acc % 256;
      this.acc = Math.floor(this.acc / 256);
      this.accBits -= 8;
    }
  }

  writeBool(value: boolean): void {
    this.writeBits(value ? 1 : 0, 1);
  }

  /** Full-precision f32, bit-packed (no alignment requirement). */
  writeFloat(value: number): void {
    scratchF32[0] = value;
    this.writeBits(scratchF32AsU32[0]!, 32);
  }

  /** Pad with zero bits up to the next byte boundary. */
  align(): void {
    if (this.accBits > 0) this.writeBits(0, 8 - this.accBits);
  }

  /**
   * Emit the trailing partial byte and return the packet length in bytes.
   * Idempotent, but writing after finish() would resume mid-byte — don't.
   */
  finish(): number {
    if (this.accBits > 0) {
      if (this.bytePos >= this.capacity) {
        this.overflow = true;
      } else {
        this.bytes[this.bytePos++] = this.acc % 256;
      }
      this.acc = 0;
      this.accBits = 0;
    }
    return this.bytePos;
  }

  /**
   * Finish and return a view of the packet.
   *
   * The subarray is a small view object, not a copy of the payload — it is the
   * one allocation in the send path and it exists because every transport wants
   * a length-correct view. Hoist it if a profile ever says it matters.
   */
  finishToBytes(): Uint8Array {
    const len = this.finish();
    return this.bytes.subarray(0, len);
  }

  /** The whole backing buffer. Use with byteLength; prefer finishToBytes(). */
  get buffer(): Uint8Array {
    return this.bytes;
  }
}

// ---------------------------------------------------------------------------
// Reader
// ---------------------------------------------------------------------------

export class BitReader {
  private bytes: Uint8Array;
  private capacity: number;
  private bytePos = 0;
  private acc = 0;
  private accBits = 0;

  /**
   * Set once a read has run past the end of the packet. Reads then return 0.
   *
   * This is the anti-cheat-relevant guard (§2.9): a truncated or hostile packet
   * must never make the reader throw or read adjacent memory. Callers check
   * `overflow` once at the end of decode and drop the packet.
   */
  overflow = false;

  constructor(src: Uint8Array) {
    this.bytes = src;
    this.capacity = src.length;
  }

  /** Rebind to another packet without allocating a new reader. */
  reset(src: Uint8Array): void {
    this.bytes = src;
    this.capacity = src.length;
    this.bytePos = 0;
    this.acc = 0;
    this.accBits = 0;
    this.overflow = false;
  }

  /** Bits consumed so far. */
  get bitsRead(): number {
    return this.bytePos * 8 - this.accBits;
  }

  get bytesTotal(): number {
    return this.capacity;
  }

  readBits(bits: number): number {
    if (bits <= 0 || bits > 32) {
      throw new RangeError(`BitReader.readBits: bits must be 1..32, got ${bits}`);
    }

    while (this.accBits < bits) {
      let byte = 0;
      if (this.bytePos < this.capacity) {
        byte = this.bytes[this.bytePos]!;
      } else {
        this.overflow = true;
      }
      this.bytePos++;
      // accBits ≤ 31 at this point, so acc stays < 2^40 — exact in f64.
      this.acc += byte * TWO_POW[this.accBits]!;
      this.accBits += 8;
    }

    const mask = TWO_POW[bits]!;
    const result = this.acc % mask;
    this.acc = Math.floor(this.acc / mask);
    this.accBits -= bits;
    return result;
  }

  readBool(): boolean {
    return this.readBits(1) === 1;
  }

  readFloat(): number {
    scratchF32AsU32[0] = this.readBits(32);
    return scratchF32[0]!;
  }

  /** Discard bits up to the next byte boundary — mirrors BitWriter.align(). */
  align(): void {
    const rem = this.bitsRead % 8;
    if (rem !== 0) this.readBits(8 - rem);
  }
}
